import * as React from "react";

import { Accordion, StatefulPanel } from "kanopi-components/Accordion";
import { HeadingSmall, ParagraphSmall } from "kanopi-components/typography";

import { ActiveCoverSections } from "kw/util/CoverSection/ActiveCoverSections";
import { Block } from "baseui/block";
import { BlockGrid } from "kanopi-components/BlockGrid";
import { Button } from "kanopi-components/Button";
import { Checkbox } from "kanopi-components/Checkbox";
import { CoverLimitations } from "kw/util/CoverLimitations/CoverLimitations";
import { InactiveCoverSections } from "kw/util/CoverSection/InactiveCoverSections";
import { QuoteDocumentPollList } from "kw/util/DocumentPollList/DocumentPollList";
import { QuotePrice } from "./QuotePrice";
import { QuoteState } from "kw/api/zeus";
import { Sektion } from "kanopi-components/Layout/Sektion";
import { useStyletron } from "kanopi-components/theme/useStyletron";

type ProductsLegacyProps = {
  riskProfile: TRiskProfile;
  quotes: TQuote[];
  disabledCoverSections: TCoverSection[];
  priceLoading: boolean;
  pricingFailed: boolean;
  priceRiskProfile: () => void;
  showProductToggle: boolean;
  enabledProductKeys: string[];
  togglingEnabledProducts: boolean;
  toggleEnabledProducts: (productKey: string, enabled: boolean) => void;
};
export const ProductsLegacy: React.FC<ProductsLegacyProps> = ({
  riskProfile,
  quotes,
  disabledCoverSections,
  priceLoading,
  pricingFailed,
  priceRiskProfile,
  showProductToggle,
  enabledProductKeys,
  toggleEnabledProducts,
  togglingEnabledProducts,
}) => {
  const [panelTitle, setPanelTitle] = React.useState<string>(
    quotes?.length === 1 ? "Hide details" : "View details"
  );
  const [, theme] = useStyletron();
  const inactiveCoverSectionsRef = React.useRef<HTMLDivElement>(null);
  return (
    <Accordion>
      {riskProfile?.availableProducts?.map((availableProduct, index) => {
        const quote = quotes.find((q) => q?.product?.key === availableProduct?.key);

        const isQuoteInKnockout = quote?.state === QuoteState.KNOCKOUT;
        return (
          quote && (
            <Block
              key={index}
              data-testid={quote?.product?.key + "-accordion"}
              paddingTop={index > 0 ? theme.sizing.scale1000 : 0}
            >
              <StatefulPanel
                initialState={{
                  expanded: quotes?.length === 1,
                }}
                overrides={{
                  Content: {
                    style: ({ $theme }) => ({
                      backgroundColor: isQuoteInKnockout
                        ? theme.colors.negative50
                        : $theme.colors.backgroundSecondary,
                      borderRadius: $theme.borders.radius200,
                    }),
                  },
                  Header: {
                    style: ({ $theme }) => ({
                      backgroundColor: isQuoteInKnockout
                        ? theme.colors.negative50
                        : $theme.colors.backgroundSecondary,
                      maxWidth: "470px",
                      margin: "auto",
                      borderRadius: `${$theme.borders.radius200} ${$theme.borders.radius200} 0 0`,
                      paddingLeft: "40px",
                      paddingRight: "40px",
                    }),
                  },
                  ToggleIcon: {
                    style: {
                      display: "none",
                    },
                  },
                }}
                title={
                  <BlockGrid
                    columns={["1", "1", "2"]}
                    width={"100%"}
                    justifyContent={[null, null, "space-between"]}
                    justifyItems={["center", "center", "start"]}
                    data-testid={quote?.product?.key + "-toggleAccordion"}
                  >
                    <Block
                      display="flex"
                      flexDirection="column"
                      justifyContent="space-between"
                      alignItems={["center", "center", "flex-start"]}
                      flex={1}
                    >
                      <HeadingSmall margin={0}>{quote?.product?.name}</HeadingSmall>
                      <HeadingSmall margin={0}>
                        {isQuoteInKnockout ? (
                          <ParagraphSmall>
                            Sorry we are unable to provide a quote for this product
                          </ParagraphSmall>
                        ) : (
                          <QuotePrice
                            inline={true}
                            highlight={false}
                            periodicPrice={quote?.periodicPrice}
                            paymentFrequency={quote?.paymentFrequency}
                            loading={priceLoading || togglingEnabledProducts}
                            pricingFailed={pricingFailed}
                            priceRiskProfile={priceRiskProfile}
                            productKey={quote?.product?.key}
                          ></QuotePrice>
                        )}
                      </HeadingSmall>

                      {showProductToggle && !isQuoteInKnockout && (
                        <Block data-testid={quote?.product?.key + "-toggle"}>
                          <Checkbox
                            checked={enabledProductKeys.includes(quote?.product?.key)}
                            onChange={(enabled: boolean) => {
                              toggleEnabledProducts(quote?.product?.key, enabled);
                            }}
                            disabled={
                              togglingEnabledProducts ||
                              priceLoading ||
                              (enabledProductKeys?.length <= 1 &&
                                enabledProductKeys.includes(quote?.product?.key))
                            }
                            checkmarkType="toggle"
                          />
                        </Block>
                      )}
                    </Block>
                    <Block
                      gridColumn={["1", " 1", "2"]}
                      alignSelf="center"
                      justifySelf={["center", "center", "end"]}
                    >
                      <Button
                        size={Button.SIZE.mini}
                        shape={Button.SHAPE.pill}
                        kind={Button.KIND.primary}
                        overrides={{
                          BaseButton: { style: { height: "min-content" } },
                        }}
                      >
                        {panelTitle}
                      </Button>
                    </Block>
                  </BlockGrid>
                }
                onChange={({ expanded }) => {
                  setPanelTitle(expanded ? "Hide details" : "View details");
                }}
              >
                {isQuoteInKnockout ? (
                  <ParagraphSmall>{`Based on your answers we are unable to offer you ${quote?.product?.name}`}</ParagraphSmall>
                ) : (
                  <Sektion compact>
                    {quote?.covers?.length > 0 && (
                      <>
                        <HeadingSmall marginTop={0} center>
                          Cover included
                        </HeadingSmall>
                        <ActiveCoverSections
                          inactiveSectionRef={inactiveCoverSectionsRef}
                          coverSections={quote?.covers}
                          disabled={priceLoading || isQuoteInKnockout}
                          prefix="/quote/ready"
                          riskProfile={riskProfile}
                          allowAdd={false}
                        />
                      </>
                    )}
                    {disabledCoverSections?.length > 0 && (
                      <>
                        <HeadingSmall marginTop={0} center>
                          Add a cover
                        </HeadingSmall>
                        <InactiveCoverSections
                          coverSections={disabledCoverSections?.filter((dc) => {
                            return dc?.product?.key === quote?.product?.key;
                          })}
                          prefix="/quote/ready"
                          riskProfile={riskProfile}
                          disabled={priceLoading || isQuoteInKnockout}
                        />
                      </>
                    )}
                    <Block data-testid={quote?.product?.key + "-coverLimitations"}>
                      <CoverLimitations limitations={quote?.limitations} />
                    </Block>
                    <Block width="100%">
                      <QuoteDocumentPollList
                        label="Important documents"
                        riskProfileId={riskProfile?.id}
                        shouldPollDocuments={false}
                        quoteId={quote?.id}
                      />
                    </Block>
                  </Sektion>
                )}
              </StatefulPanel>
            </Block>
          )
        );
      })}
    </Accordion>
  );
};
